import { COMPILED_COLLISION_CELLS_PER_TILE, frameId, propFeet, stateFootprint, type CompileContext, type MapDocument, type ObjectTypeDefinition, type PropInstance } from "daedalus-core";
import { newCellBounds, stampThing, thingCellBounds, type CellBounds, type ThingStamp } from "../collision/things.ts";
import { statesAPlayerReaches } from "../interaction/passage.ts";
import { SIXTEENTHS } from "../movement/step.ts";

/**
 * The cells a map's things block whatever a player does (ADR-096's amendment of 2026-10-13): for a compile's
 * check that floods the collision grid, which a published chunk carries with no thing stamped. A thing is a
 * prop with a behaviour that the compile lowers into an entity; one driven by presence (a roof) stays a prop
 * and blocks nothing. Each thing marks the cells that every state a player can bring it to blocks
 * (`statesAPlayerReaches`), laid as the map server lays a thing's footprint (`stampThing`), so a gate marks its
 * posts, and a well, a chest, a dummy and a shrine mark what they block as they stand. Pure; run where a
 * document is compiled, never on an edit.
 */

/**
 * A thing as the game stamps it in one of its states: its feet point from the placed frame, the state's
 * footprint and the prop's mirroring. The feet point is held exactly and carries no pixel offset, which lays the
 * footprint where the server's rounded feet point and its appearance's offset lay it.
 *
 * @param prop The placed prop.
 * @param context The compile context.
 * @param tileSize Tile size in pixels.
 * @param stateId The state.
 * @returns The stamp, or null when the context cannot size the placed frame or the state blocks nothing.
 * @throws Error when a further state has no frame or its cells cannot be sized, as `stateFootprint` throws.
 */
function stampOf(prop: PropInstance, context: CompileContext, tileSize: number, stateId: string): ThingStamp | null {
  const placed = frameId(prop.frame.tileset, null, prop.frame.tile);
  const width = (context.frameSizes?.[placed] ?? context.collision.footprints?.[placed])?.w;
  const footprint = stateFootprint(prop, context, tileSize, stateId);
  if (width === undefined || footprint === null) {
    return null;
  }
  const feet = propFeet(prop, width, tileSize);
  return { x: feet.x * SIXTEENTHS, y: feet.y * SIXTEENTHS, footprint, flipX: prop.flipX, flipY: prop.flipY };
}

/**
 * Marks on a collision grid the cells each thing of a map blocks in every state a player can bring it to. A
 * thing with one such state marks that state's footprint; one with several marks only the cells all of them
 * mark, so a thing with a state that blocks nothing marks nothing.
 *
 * @param cells The map's collision cells, `COMPILED_COLLISION_CELLS_PER_TILE` a tile each way (the grid `resolveMap` compiles), marked in place.
 * @param map The map.
 * @param context The compile context: the object types as the compile lowers them, the frames' footprints and
 *   sizes.
 * @param types The game's object types, for their verbs; a behaviour whose type is not among them blocks as it
 *   stands.
 * @param tileSize Tile size in pixels.
 * @param value Byte to OR in for a blocked cell.
 * @throws Error when a thing's further state has no frame or its cells cannot be sized, as `stateFootprint`
 *   throws; a compile refuses such a document before this runs.
 */
export function stampBlockingThings(cells: Uint8Array, map: MapDocument, context: CompileContext, types: readonly ObjectTypeDefinition[], tileSize: number, value: number): void {
  const gridWidth = map.width * COMPILED_COLLISION_CELLS_PER_TILE;
  const gridHeight = map.height * COMPILED_COLLISION_CELLS_PER_TILE;
  const one = new Uint8Array(cells.length);
  const count = new Uint8Array(cells.length);
  const bounds = newCellBounds();
  for (const layer of map.layers) {
    if (layer.kind !== "prop") {
      continue;
    }
    for (const prop of layer.props) {
      const lowered = context.objectTypes.find((type) => type.type === prop.behaviour?.type);
      if (lowered === undefined || lowered.inside !== undefined) {
        continue;
      }
      const definition = types.find((type) => type.type === lowered.type);
      const states = definition === undefined ? [lowered.initial] : statesAPlayerReaches(definition);
      const union: CellBounds = { left: gridWidth, top: gridHeight, right: -1, bottom: -1 };
      let open = false;
      for (const stateId of states) {
        const stamp = stampOf(prop, context, tileSize, stateId);
        if (stamp === null || !thingCellBounds(stamp, tileSize, COMPILED_COLLISION_CELLS_PER_TILE, bounds)) {
          open = true;
          continue;
        }
        const left = Math.max(0, bounds.left);
        const top = Math.max(0, bounds.top);
        const right = Math.min(gridWidth - 1, bounds.right);
        const bottom = Math.min(gridHeight - 1, bounds.bottom);
        stampThing(one, map.width, map.height, stamp, tileSize, COMPILED_COLLISION_CELLS_PER_TILE, 1);
        for (let row = top; row <= bottom; row += 1) {
          for (let at = row * gridWidth + left; at <= row * gridWidth + right; at += 1) {
            count[at] = (count[at] as number) + (one[at] as number);
            one[at] = 0;
          }
        }
        union.left = Math.min(union.left, left);
        union.top = Math.min(union.top, top);
        union.right = Math.max(union.right, right);
        union.bottom = Math.max(union.bottom, bottom);
      }
      for (let row = union.top; row <= union.bottom; row += 1) {
        for (let at = row * gridWidth + union.left; at <= row * gridWidth + union.right; at += 1) {
          if (!open && count[at] === states.length) {
            cells[at] = (cells[at] as number) | value;
          }
          count[at] = 0;
        }
      }
    }
  }
}
